import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContract } from '../../context/ContractContext';
import Input from '../common/Input';
import Select from '../common/Select';
import EbesSolarPreview from './EbesSolarPreview';
import EbesClausesManager from './EbesClausesManager';

const EbesSolarForm = () => {
  const navigate = useNavigate();
  const { updateContractData } = useContract();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPreview, setShowPreview] = useState(true);

  const [formData, setFormData] = useState({
    // Tipo de Cliente
    clientType: 'pj', // 'pj' ou 'pf'
    
    // Dados do Cliente
    clientName: '',
    clientCNPJ: '',
    clientCPF: '',
    clientAddress: '',
    clientCity: 'São Luis',
    clientState: 'MA',
    clientCEP: '',
    
    // Dados da Instalação
    installationAddress: '',
    installationCity: 'São Luis',
    installationState: 'MA',
    installationCEP: '',
    accountHolderName: '',
    accountHolderCPF: '',
    compensationMode: 'geracao_junto_carga',
    beneficiaryUnits: '2',
    
    // Dados Financeiros
    systemPower: '',
    totalValue: '',
    firstPaymentPercent: '50',
    firstPaymentValue: '',
    secondPaymentPercent: '50',
    secondPaymentValue: '',
    firstPaymentEvent: 'assinatura',
    secondPaymentEvent: 'entrega_modulos',
    
    // Dados Técnicos
    warrantyPeriod: '1',
    moduleWarranty: '12',
    inverterWarranty: '10',
    installationDeadline: '20',
    
    // Cláusulas Personalizadas - NOVO
    customClauses: [],
    
    // Informações Adicionais
    observations: ''
  });

  const steps = [
    { id: 1, name: 'Dados do Cliente' },
    { id: 2, name: 'Instalação' },
    { id: 3, name: 'Condições Financeiras' },
    { id: 4, name: 'Cláusulas e Observações' },
    { id: 5, name: 'Revisão e Finalização' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Calcular valores automaticamente
    if (name === 'totalValue' || name === 'firstPaymentPercent') {
      const total = name === 'totalValue' ? parseFloat(value) : parseFloat(formData.totalValue);
      const percent = name === 'firstPaymentPercent' ? parseFloat(value) : parseFloat(formData.firstPaymentPercent);
      
      if (total && percent) {
        const firstValue = (total * percent / 100).toFixed(2);
        const secondValue = (total - firstValue).toFixed(2);
        
        setFormData(prev => ({
          ...prev,
          firstPaymentValue: firstValue,
          secondPaymentValue: secondValue,
          secondPaymentPercent: (100 - percent).toString()
        }));
      }
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Salvar dados no contexto
    updateContractData('ebesSolar', formData);
    updateContractData('type', 'ebes_solar');
    
    // Redirecionar para geração do PDF
    navigate('/contratos/gerar-ebes');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const compensationModes = [
    { value: 'geracao_junto_carga', label: 'Geração Junto à Carga' },
    { value: 'autoconsumo_remoto', label: 'Autoconsumo Remoto' },
    { value: 'geracao_compartilhada', label: 'Geração Compartilhada' },
    { value: 'multiplas_unidades', label: 'Múltiplas Unidades Consumidoras' }
  ];

  const paymentEvents = [
    { value: 'assinatura', label: 'Assinatura do Contrato' },
    { value: 'entrega_modulos', label: 'Entrega dos Módulos' },
    { value: 'instalacao', label: 'Instalação Completa' },
    { value: 'homologacao', label: 'Homologação pela Concessionária' }
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        if (formData.clientType === 'pj') {
          return formData.clientName && formData.clientCNPJ && formData.clientAddress;
        } else {
          return formData.clientName && formData.clientCPF && formData.clientAddress;
        }
      case 2:
        return formData.installationAddress && formData.accountHolderName && formData.accountHolderCPF;
      case 3:
        return formData.systemPower && formData.totalValue;
      case 4:
        return true; // Cláusulas são opcionais
      default:
        return true;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-dark-900">Dados do Cliente</h3>
            
            {/* Seletor de Tipo de Cliente */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-dark-800 mb-3">
                Tipo de Cliente
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="clientType"
                    value="pj"
                    checked={formData.clientType === 'pj'}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm font-medium text-dark-800">
                    Pessoa Jurídica (CNPJ)
                  </span>
                </label>
                
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="clientType"
                    value="pf"
                    checked={formData.clientType === 'pf'}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm font-medium text-dark-800">
                    Pessoa Física (CPF)
                  </span>
                </label>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Input
                  label={formData.clientType === 'pj' ? 'Razão Social' : 'Nome Completo'}
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleChange}
                  required
                />
              </div>

              {formData.clientType === 'pj' ? (
                <Input
                  label="CNPJ"
                  name="clientCNPJ"
                  value={formData.clientCNPJ}
                  onChange={handleChange}
                  mask="99.999.999/9999-99"
                  placeholder="00.000.000/0000-00"
                  required
                />
              ) : (
                <Input
                  label="CPF"
                  name="clientCPF"
                  value={formData.clientCPF}
                  onChange={handleChange}
                  mask="999.999.999-99"
                  placeholder="000.000.000-00"
                  required
                />
              )}

              <Input
                label="CEP"
                name="clientCEP"
                value={formData.clientCEP}
                onChange={handleChange}
                mask="99999-999"
                placeholder="00000-000"
              />

              <div className="md:col-span-2">
                <Input
                  label="Endereço Completo"
                  name="clientAddress"
                  value={formData.clientAddress}
                  onChange={handleChange}
                  placeholder="Avenida, número, bairro"
                  required
                />
              </div>

              <Input
                label="Cidade"
                name="clientCity"
                value={formData.clientCity}
                onChange={handleChange}
                required
              />

              <Input
                label="Estado"
                name="clientState"
                value={formData.clientState}
                onChange={handleChange}
                maxLength={2}
                required
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-dark-900">Dados da Instalação</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Input
                  label="Endereço de Instalação"
                  name="installationAddress"
                  value={formData.installationAddress}
                  onChange={handleChange}
                  placeholder="Local onde será instalada a usina fotovoltaica"
                  required
                />
              </div>

              <Input
                label="Cidade"
                name="installationCity"
                value={formData.installationCity}
                onChange={handleChange}
                required
              />

              <Input
                label="Estado"
                name="installationState"
                value={formData.installationState}
                onChange={handleChange}
                maxLength={2}
                required
              />

              <Input
                label="CEP"
                name="installationCEP"
                value={formData.installationCEP}
                onChange={handleChange}
                mask="99999-999"
                placeholder="00000-000"
              />

              <Input
                label="Titular da Conta de Luz"
                name="accountHolderName"
                value={formData.accountHolderName}
                onChange={handleChange}
                required
              />

              <Input
                label="CPF/CNPJ do Titular"
                name="accountHolderCPF"
                value={formData.accountHolderCPF}
                onChange={handleChange}
                placeholder="000.000.000-00"
                required
              />

              <Select
                label="Modalidade de Compensação"
                name="compensationMode"
                value={formData.compensationMode}
                onChange={handleChange}
                options={compensationModes}
              />

              <Input
                label="Unidades Consumidoras Beneficiárias"
                name="beneficiaryUnits"
                type="number"
                value={formData.beneficiaryUnits}
                onChange={handleChange}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-dark-900">Condições Financeiras</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Potência da Usina (kWp)"
                name="systemPower"
                type="number"
                step="0.01"
                value={formData.systemPower}
                onChange={handleChange}
                placeholder="Ex: 9.99"
                required
              />

              <Input
                label="Valor Total do Sistema"
                name="totalValue"
                type="number"
                step="0.01"
                value={formData.totalValue}
                onChange={handleChange}
                placeholder="0.00"
                helperText={formatCurrency(formData.totalValue)}
                required
              />

              <div className="md:col-span-2 border-t pt-4">
                <h4 className="font-medium text-dark-900 mb-4">Forma de Pagamento</h4>
              </div>

              <Input
                label="1ª Parcela (%)"
                name="firstPaymentPercent"
                type="number"
                value={formData.firstPaymentPercent}
                onChange={handleChange}
                placeholder="50"
              />

              <Input
                label="Valor da 1ª Parcela"
                name="firstPaymentValue"
                type="number"
                value={formData.firstPaymentValue}
                readOnly
                helperText={formatCurrency(formData.firstPaymentValue)}
              />

              <Select
                label="Evento de Pagamento 1ª Parcela"
                name="firstPaymentEvent"
                value={formData.firstPaymentEvent}
                onChange={handleChange}
                options={paymentEvents}
              />

              {formData.firstPaymentPercent < 100 && (
                <>
                  <Input
                    label="2ª Parcela (%)"
                    name="secondPaymentPercent"
                    type="number"
                    value={formData.secondPaymentPercent}
                    readOnly
                  />

                  <Input
                    label="Valor da 2ª Parcela"
                    name="secondPaymentValue"
                    type="number"
                    value={formData.secondPaymentValue}
                    readOnly
                    helperText={formatCurrency(formData.secondPaymentValue)}
                  />

                  <Select
                    label="Evento de Pagamento 2ª Parcela"
                    name="secondPaymentEvent"
                    value={formData.secondPaymentEvent}
                    onChange={handleChange}
                    options={paymentEvents}
                  />
                </>
              )}

              <div className="md:col-span-2 border-t pt-4">
                <h4 className="font-medium text-dark-900 mb-4">Prazos e Garantias</h4>
              </div>

              <Input
                label="Prazo de Instalação (dias úteis)"
                name="installationDeadline"
                type="number"
                value={formData.installationDeadline}
                onChange={handleChange}
              />

              <Input
                label="Garantia de Instalação (anos)"
                name="warrantyPeriod"
                type="number"
                value={formData.warrantyPeriod}
                onChange={handleChange}
              />

              <Input
                label="Garantia dos Módulos (anos)"
                name="moduleWarranty"
                type="number"
                value={formData.moduleWarranty}
                onChange={handleChange}
              />

              <Input
                label="Garantia dos Inversores (anos)"
                name="inverterWarranty"
                type="number"
                value={formData.inverterWarranty}
                onChange={handleChange}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-dark-900">Cláusulas e Observações</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Personalize o contrato adicionando cláusulas específicas e observações
                </p>
              </div>
            </div>

            {/* Gerenciador de Cláusulas */}
            <EbesClausesManager 
              formData={formData} 
              updateFormData={setFormData} 
            />

            {/* Observações */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">
                📝 Observações Adicionais
              </h4>
              <div>
                <label className="block text-sm font-medium text-dark-800 mb-2">
                  Observações Gerais (Opcional)
                </label>
                <textarea
                  name="observations"
                  rows={4}
                  value={formData.observations}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Informações adicionais relevantes para o contrato..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.observations?.length || 0}/500 caracteres
                </p>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-dark-900 mb-4">Revisão dos Dados</h3>

            {/* Resumo Cliente */}
            <div className="bg-white rounded-lg border p-6">
              <h4 className="font-semibold text-dark-900 mb-3">👤 Cliente</h4>
              <div className="space-y-2 text-sm">
                <p><strong>Tipo:</strong> {formData.clientType === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}</p>
                <p><strong>Nome:</strong> {formData.clientName}</p>
                <p><strong>{formData.clientType === 'pj' ? 'CNPJ' : 'CPF'}:</strong> {formData.clientType === 'pj' ? formData.clientCNPJ : formData.clientCPF}</p>
                <p><strong>Endereço:</strong> {formData.clientAddress}, {formData.clientCity}/{formData.clientState}</p>
              </div>
            </div>

            {/* Resumo Instalação */}
            <div className="bg-white rounded-lg border p-6">
              <h4 className="font-semibold text-dark-900 mb-3">⚡ Instalação</h4>
              <div className="space-y-2 text-sm">
                <p><strong>Local:</strong> {formData.installationAddress}</p>
                <p><strong>Titular da Conta:</strong> {formData.accountHolderName}</p>
                <p><strong>Modalidade:</strong> {compensationModes.find(m => m.value === formData.compensationMode)?.label}</p>
                <p><strong>Unidades Beneficiárias:</strong> {formData.beneficiaryUnits}</p>
              </div>
            </div>

            {/* Resumo Financeiro */}
            <div className="bg-primary-50 rounded-lg border border-primary-200 p-6">
              <h4 className="font-semibold text-primary-900 mb-3">💰 Resumo Financeiro</h4>
              <div className="space-y-2 text-sm">
                <p><strong>Potência:</strong> {formData.systemPower} kWp</p>
                <p><strong>Valor Total:</strong> {formatCurrency(formData.totalValue)}</p>
                <p><strong>1ª Parcela ({formData.firstPaymentPercent}%):</strong> {formatCurrency(formData.firstPaymentValue)}</p>
                <p className="text-xs text-primary-700 ml-4">
                  💳 {paymentEvents.find(e => e.value === formData.firstPaymentEvent)?.label}
                </p>
                {formData.firstPaymentPercent < 100 && (
                  <>
                    <p><strong>2ª Parcela ({formData.secondPaymentPercent}%):</strong> {formatCurrency(formData.secondPaymentValue)}</p>
                    <p className="text-xs text-primary-700 ml-4">
                      💳 {paymentEvents.find(e => e.value === formData.secondPaymentEvent)?.label}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Resumo Cláusulas */}
            {formData.customClauses?.length > 0 && (
              <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-6">
                <h4 className="font-semibold text-yellow-900 mb-3">
                  📋 Cláusulas Personalizadas ({formData.customClauses.length})
                </h4>
                <div className="space-y-2 text-sm">
                  {formData.customClauses.map((clause, index) => (
                    <div key={clause.id} className="border-l-3 border-yellow-400 pl-3">
                      <p className="font-medium text-yellow-900">{index + 10}. {clause.title}</p>
                      <p className="text-yellow-700 text-xs">{clause.content.substring(0, 100)}...</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Observações */}
            {formData.observations && (
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
                <h4 className="font-semibold text-blue-900 mb-3">📝 Observações</h4>
                <p className="text-sm text-blue-800">{formData.observations}</p>
              </div>
            )}

            {/* Alerta de Confirmação */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h5 className="font-medium text-green-900 mb-1">Tudo pronto para gerar o contrato!</h5>
                  <p className="text-sm text-green-700">
                    Revise todas as informações acima e clique em "Gerar Contrato" para criar o PDF final.
                    O contrato será baixado automaticamente após a geração.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <h1 className="text-2xl font-bold text-dark-900">
                  Novo Contrato EBES Solar
                </h1>
              </div>
              <p className="text-neutral-600">
                Contrato de Fornecimento e Instalação de Usina Fotovoltaica
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center px-3 py-2 text-sm text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                {showPreview ? (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                    Ocultar Preview
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Mostrar Preview
                  </>
                )}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-neutral-500 hover:text-neutral-700"
              >
                Cancelar
              </button>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => step.id <= currentStep && setCurrentStep(step.id)}
                    disabled={step.id > currentStep}
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold
                      transition-all duration-200 border
                      ${step.id === currentStep 
                        ? 'bg-primary-500 text-white border-primary-500' 
                        : step.id < currentStep
                          ? 'bg-success-500 text-white border-success-500'
                          : 'bg-neutral-200 text-neutral-600 border-neutral-300'
                      }
                    `}
                  >
                    {step.id < currentStep ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      step.id
                    )}
                  </button>
                  <span className={`
                    mt-2 text-xs text-center max-w-20
                    ${step.id === currentStep ? 'text-primary-600 font-medium' : 'text-neutral-500'}
                  `}>
                    {step.name}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`
                    flex-1 h-0.5 mx-4 transition-colors duration-200
                    ${step.id < currentStep ? 'bg-success-500' : 'bg-neutral-200'}
                  `} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content with Preview */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Form Section */}
          <div className={`${showPreview ? 'w-1/2' : 'w-full max-w-4xl mx-auto'} transition-all duration-300`}>
            <div className="bg-white rounded-lg shadow-sm border p-6">
              {renderStepContent()}

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t">
                <button
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className={`
                    flex items-center px-4 py-2 rounded-lg transition-colors
                    ${currentStep === 1 
                      ? 'text-neutral-400 cursor-not-allowed bg-neutral-100' 
                      : 'text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100 bg-white border border-neutral-300'
                    }
                  `}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Anterior
                </button>

                {currentStep === steps.length ? (
                  <button
                    onClick={handleSubmit}
                    disabled={!canProceed()}
                    className={`
                      flex items-center px-6 py-3 rounded-lg font-medium transition-all
                      ${canProceed()
                        ? 'bg-success-500 hover:bg-success-600 text-white shadow-sm'
                        : 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                      }
                    `}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Gerar Contrato
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className={`
                      flex items-center px-6 py-3 rounded-lg font-medium transition-all
                      ${canProceed()
                        ? 'bg-primary-500 hover:bg-primary-600 text-white shadow-sm'
                        : 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                      }
                    `}
                  >
                    Próximo
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Preview Section */}
          {showPreview && (
            <div className="w-1/2">
              <div className="sticky top-8">
                <div className="bg-white rounded-lg shadow-sm border">
                  <div className="bg-gray-50 px-4 py-3 border-b rounded-t-lg">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Preview do Contrato
                    </h3>
                  </div>
                  <div className="max-h-[800px] overflow-y-auto">
                    <EbesSolarPreview formData={formData} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EbesSolarForm;